'use client'

import { useParams } from 'next/navigation'
import { useMemo, useRef } from 'react'

import { useCatalogInitialData } from '@/features/catalog/catalog-initial-data'

import {
	FilterJsonType,
	FilterProperties,
	StepFilterProperty,
	useGetCatalogStepFilterQuery
} from '@/entities/catalog-old'
import { getRegionCookies } from '@/entities/regions'

type UseStepFilterProps = {
	filterUrl: string
	onFilterChange?: (value: string) => void | Promise<void>
	// Sidebar filter data — the fast, already-fetched source the step chips are derived from.
	properties?: FilterProperties[]
	isFilterLoading?: boolean
}

const getCategoryCodeFromSlug = (slug: string | string[] | undefined) => (Array.isArray(slug) ? slug[0] : slug)

// The step-filter endpoint accepts the same JSON we send to the products query, but the canonical
// products filter carries LIST_SECTION_CODE (the category). onFilterChange, mirroring the sidebar
// form/applied-filters, expects the user selection WITHOUT LIST_SECTION_CODE — it re-adds it. So we
// strip it here before merging the newly picked step value.
const getSelectionFromFilterUrl = (filterUrl: string): Record<string, unknown> => {
	try {
		const parsed = JSON.parse(filterUrl) as Record<string, unknown>
		delete parsed.LIST_SECTION_CODE
		return parsed
	} catch {
		return {}
	}
}

// A step is "picked" when the live client selection has a value for its code. We read SELECTED from
// the optimistic selection (filterUrl), NOT the server `CHECKED`, so the active step advances (or
// resets to step 1 on "Убрать все") instantly — without waiting for the sef-url → filter refetch.
const hasSelectedValue = (selection: Record<string, unknown>, code: string): boolean => {
	const entry = selection[code] as { value?: unknown[] } | undefined
	return Array.isArray(entry?.value) && entry.value.length > 0
}

// Project a sidebar filter property onto the step-filter shape the UI consumes. Steps are just a
// curated, ordered subset of the same properties (server section field UF_FILTER_PROPS); every field
// the chips need (VALUE, ID, ELEMENT_COUNT, CHECKED) already lives in the sidebar response.
const toStepProperty = (prop: FilterProperties, selected: boolean, nameHint: string): StepFilterProperty => ({
	ID: Number(prop.ID) || 0,
	CODE: prop.CODE,
	NAME: prop.NAME,
	// NAME_HINT isn't in the sidebar filter response — it comes from the step-filter endpoint (SSR
	// initial data), looked up by CODE. Fall back to NAME so the label never goes blank.
	NAME_HINT: nameHint || prop.NAME,
	FILTER_HINT: prop.FILTER_HINT,
	SELECTED: selected,
	VALUES: prop.VALUES.map(value => ({
		ID: value.ID,
		NAME: value.NAME,
		VALUE: value.VALUE,
		ELEMENT_COUNT: value.ELEMENT_COUNT,
		CHECKED: Boolean(value.CHECKED)
	}))
})

export const useStepFilter = ({
	filterUrl,
	onFilterChange,
	properties,
	isFilterLoading = false
}: UseStepFilterProps) => {
	const params = useParams()
	const categoryCode = getCategoryCodeFromSlug(params?.slug as string | string[] | undefined) ?? ''
	const regionCookies = getRegionCookies()

	const { initialStepFilterData } = useCatalogInitialData()

	// The ordered step CODE list is a stable per-category config (UF_FILTER_PROPS), delivered once via
	// SSR (prefetchCatalogStepFilter → initial data). Everything else is derived live from the sidebar
	// filter, so we never hit the dedicated step endpoint on interaction — that removed per-click
	// round-trip is the whole point of this change.
	const ssrStepCodes = useMemo(() => (initialStepFilterData ?? []).map(step => step.CODE), [initialStepFilterData])
	const hasSsrSteps = ssrStepCodes.length > 0

	// NAME_HINT (the "Выберите …" accusative prompt) only ships on the step-filter endpoint, not on the
	// sidebar filter the steps are otherwise derived from — so index it by CODE from the SSR step data.
	const stepHintByCode = useMemo(
		() => new Map((initialStepFilterData ?? []).map(step => [step.CODE, step.NAME_HINT])),
		[initialStepFilterData]
	)

	// Fallback path ONLY when SSR gave us no step config (e.g. backend not populated): keep the legacy
	// dedicated endpoint so behaviour is preserved. When SSR steps exist we skip it entirely.
	const { data, currentData, isFetching } = useGetCatalogStepFilterQuery(
		{ categoryCode, filterUrl, regionCookies },
		// refetchOnFocus is enabled globally on apiSlice; the catalog shouldn't re-fetch on tab refocus.
		{ skip: hasSsrSteps || !categoryCode || !filterUrl, refetchOnFocus: false }
	)
	const endpointSteps = currentData ?? data
	// Keep the previous endpoint result on screen across a refetch (fallback path only).
	const lastEndpointStepsRef = useRef<StepFilterProperty[] | undefined>(undefined)
	if (endpointSteps) lastEndpointStepsRef.current = endpointSteps

	// Live optimistic selection (the same JSON that drives the products query) — the source for
	// SELECTED, so the active step reacts to a pick/reset immediately, ahead of the server refetch.
	const selection = useMemo(() => getSelectionFromFilterUrl(filterUrl), [filterUrl])

	const steps: StepFilterProperty[] = useMemo(() => {
		if (hasSsrSteps) {
			const propByCode = new Map((properties ?? []).map(prop => [prop.CODE, prop]))
			return ssrStepCodes
				.map(code => propByCode.get(code))
				.filter((prop): prop is FilterProperties => Boolean(prop))
				.map(prop => toStepProperty(prop, hasSelectedValue(selection, prop.CODE), stepHintByCode.get(prop.CODE) ?? ''))
		}
		return endpointSteps ?? lastEndpointStepsRef.current ?? []
	}, [hasSsrSteps, ssrStepCodes, properties, selection, endpointSteps, stepHintByCode])

	// Active step = first not-yet-picked step the user can actually act on. Skip steps with no real
	// choice: empty ones (no values with products) and single-value ones. This matters for categories
	// with several parallel "модель" properties (чехлы: модель iPhone / iPad / …): once a device is
	// picked, the other model steps have only zero-count values in scope, so we skip past them instead
	// of stalling on an empty step. A lone available value is a no-op filter (everything in scope
	// already matches it), so skipping it is safe too.
	const activeStep = useMemo(
		() =>
			steps.find(step => !step.SELECTED && step.VALUES.filter(({ ELEMENT_COUNT }) => ELEMENT_COUNT > 0).length > 1) ??
			null,
		[steps]
	)

	// Ordered list of step property codes — the source of truth for cascade deletion in applied
	// filters (removing a step must also drop every step that comes after it). Use the full SSR order
	// so cascade stays correct even for steps whose property isn't currently rendered.
	const stepCodes = useMemo(
		() => (hasSsrSteps ? ssrStepCodes : steps.map(step => step.CODE)),
		[hasSsrSteps, ssrStepCodes, steps]
	)

	// Single select per step: picking a value sets it as the only value for that property, then
	// the next unpicked property becomes the active step. Same onFilterChange the sidebar uses.
	const onSelectValue = (code: string, value: string | number) => {
		const selection = getSelectionFromFilterUrl(filterUrl)
		selection[code] = { type: FilterJsonType.Default, value: [value] }
		onFilterChange?.(JSON.stringify(selection))
	}

	return {
		activeStep,
		steps,
		stepCodes,
		onSelectValue,
		isLoading: hasSsrSteps ? isFilterLoading : isFetching
	}
}
